import { DatePipe } from '@angular/common';

/**
 * Utility class for formatting dates in message center contexts
 */
export class MessageDateFormatter {
  /**
   * Legacy date formatter - shows date if more than 24 hours old or different day, otherwise shows time
   * @param date - The date to format
   * @param locale - The locale string for formatting (e.g., 'en-US')
   * @returns Formatted date/time string
   */
  static formatTime(date: Date | string | undefined, locale: string): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const pipe = new DatePipe(locale);
    return d < twentyFourHoursAgo || d.getDay() !== now.getDay()
      ? (pipe.transform(d, 'MM/dd/yyyy') ?? '')
      : (pipe.transform(d, 'h:mm a') ?? '');
  }

  /**
   * Improved date formatter with week-aware logic:
   * - If today: show only time (e.g., "2:30 PM")
   * - If this week (Sun-Sat): show day name (e.g., "Monday")
   * - If not this week: show date (e.g., "12/15/2024")
   *
   * @param date - The date to format
   * @param locale - The locale string for formatting (e.g., 'en-US')
   * @returns Formatted date/time string
   */
  static formatTimeAlt(date: Date | string | undefined, locale: string): string {
    if (!date) return '';

    const d = new Date(date);
    const now = new Date();
    const pipe = new DatePipe(locale);

    // Check if it's today
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return pipe.transform(d, 'h:mm a') ?? '';
    }

    // Check if it's in the current week (Sunday - Saturday)
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Go to Sunday
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
    endOfWeek.setHours(23, 59, 59, 999);

    const isThisWeek = d >= startOfWeek && d <= endOfWeek;
    if (isThisWeek) {
      return pipe.transform(d, 'EEEE') ?? ''; // Day name like "Monday"
    }

    // Otherwise show the date
    return pipe.transform(d, 'MM/dd/yyyy') ?? '';
  }

  /**
   * Format date and time for tooltip display
   * @param date - The date to format
   * @param locale - The locale string for formatting (e.g., 'en-US')
   * @returns Full date and time string
   */
  static formatTimeTooltip(date: Date | string | undefined, locale: string): string {
    if (!date) return '';
    const d = new Date(date);
    const pipe = new DatePipe(locale);
    return pipe.transform(d, 'MM/dd/yyyy h:mm a') ?? '';
  }

  /**
   * Convert UTC date string to local date
   * @param utcDateTimeString - UTC date time string
   * @returns Local date
   */
  static convertUtcToLocal(utcDateTimeString: string | Date): Date {
    const utcDate = new Date(utcDateTimeString);
    return new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000);
  }
}
